import { ExternalLink } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

export const inr = n => (n === null || n === undefined ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`);
export const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

export const Ext = ({ href, children, testid }) => href ? (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-violet-700 hover:underline" data-testid={testid} onClick={e => e.stopPropagation()}>{children}<ExternalLink className="w-3 h-3" /></a>
) : <span className="text-slate-300">—</span>;

const STATUS = { paid: 'bg-emerald-50 text-emerald-700', overdue: 'bg-rose-50 text-rose-700', partially_paid: 'bg-sky-50 text-sky-700' };
export const InvStatus = ({ s }) => <Badge variant="outline" className={`text-[9px] ${STATUS[s] || ''}`}>{(s || '').replace('_', ' ')}</Badge>;

const Th = ({ children, right }) => <th className={`px-3 py-2 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
const Table = ({ head, children, testid, empty }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>{head}</tr></thead>
      <tbody>{children}</tbody>
    </table>
    {empty && <p className="text-center text-sm text-slate-400 py-8">Nothing here.</p>}
  </div>
);
const Td = ({ children, right, cls = '' }) => <td className={`px-3 py-2 ${right ? 'text-right' : ''} ${cls}`}>{children}</td>;

const InvoiceCells = ({ i, id, v }) => i ? <>
  <Td><Ext href={i.url} testid={`vc-books-link-${id}`}>{i.invoice_number}</Ext><div className="text-[10px] text-slate-400">{i.date} · {i.item_name}</div>
    <div className={`text-[10px] ${v.customer_mismatch ? 'text-rose-600 font-semibold' : 'text-slate-400'}`} data-testid={`vc-match-${id}`}>{v.match_by === 'student' ? 'matched via student' : 'matched via invoice Enrolment No'}{v.customer_mismatch ? ` — customer differs from ${v.books_customer}` : ''}</div></Td>
  <Td>{i.customer_name}</Td><Td right>{inr(i.total)}</Td><Td><InvStatus s={i.status} /></Td>
</> : <Td cls="text-amber-600" >No invoice with this Enrolment No</Td>;

export const IssuedTable = ({ rows }) => (
  <Table testid="vc-table-issued" empty={!rows.length} head={<><Th>Voucher (CRM)</Th><Th>Exam</Th><Th>Enrollment</Th><Th>Student</Th><Th>Expiry</Th><Th>Books invoice</Th><Th>Customer</Th><Th right>Total</Th><Th>Status</Th></>}>
    {rows.map(v => <tr key={v.id} className="border-t border-slate-100" data-testid={`vc-issued-${v.id}`}>
      <Td><Ext href={v.url} testid={`vc-crm-link-${v.id}`}>{v.voucher_code || 'Open'}</Ext></Td>
      <Td>{v.exam}{v.exam_mismatch && <div className="text-[10px] text-amber-600">invoice is for {v.invoice.exam}</div>}</Td>
      <Td cls="font-mono">{v.enrollment_no}</Td><Td>{v.student}<div className="text-[10px] text-slate-400">{v.course}</div></Td><Td>{v.expiry_date || '—'}</Td>
      {v.invoice ? <InvoiceCells i={v.invoice} id={v.id} v={v} /> : <td colSpan={4} className="px-3 py-2 text-amber-600">No voucher invoice found for this student</td>}
    </tr>)}
  </Table>
);

export const AvailableTable = ({ rows }) => (
  <Table testid="vc-table-available" empty={!rows.length} head={<><Th>Voucher (CRM)</Th><Th>Exam</Th><Th>Brand</Th><Th>Purchase</Th><Th>Expiry</Th><Th>Owner</Th></>}>
    {rows.map(v => <tr key={v.id} className="border-t border-slate-100" data-testid={`vc-available-${v.id}`}>
      <Td><Ext href={v.url} testid={`vc-crm-link-${v.id}`}>{v.voucher_code || 'Open'}</Ext></Td><Td>{v.exam}</Td><Td>{v.brand}</Td><Td>{v.purchase_date || '—'}</Td><Td>{v.expiry_date || '—'}</Td><Td>{v.owner}</Td>
    </tr>)}
  </Table>
);

export const BilledTable = ({ rows }) => (
  <Table testid="vc-table-billed" empty={!rows.length} head={<><Th>Books invoice</Th><Th>Exam item</Th><Th>Enrolment No (invoice)</Th><Th>Customer</Th><Th>Salesperson</Th><Th right>Total</Th><Th right>Balance</Th><Th>Status</Th></>}>
    {rows.map(i => <tr key={i.invoice_id + i.item_id} className="border-t border-slate-100" data-testid={`vc-billed-${i.invoice_id}`}>
      <Td><Ext href={i.url} testid={`vc-books-link-${i.invoice_id}`}>{i.invoice_number}</Ext><div className="text-[10px] text-slate-400">{i.date}</div></Td><Td>{i.exam}</Td>
      <Td cls="font-mono">{i.enrolment_field || <span className="text-amber-600">blank</span>}</Td><Td>{i.customer_name}</Td><Td>{i.salesperson}</Td><Td right>{inr(i.total)}</Td><Td right>{inr(i.balance)}</Td><Td><InvStatus s={i.status} /></Td>
    </tr>)}
  </Table>
);

export const NotBilledTable = ({ rows }) => (
  <Table testid="vc-table-notbilled" empty={!rows.length} head={<><Th>Voucher (CRM)</Th><Th>Exam</Th><Th>Enrollment</Th><Th>Student</Th><Th>Course</Th><Th>Issued by</Th><Th>Last change</Th></>}>
    {rows.map(v => <tr key={v.id} className="border-t border-slate-100" data-testid={`vc-notbilled-${v.id}`}>
      <Td><Ext href={v.url} testid={`vc-crm-link-${v.id}`}>{v.voucher_code || 'Open'}</Ext></Td><Td>{v.exam}</Td><Td cls="font-mono">{v.enrollment_no}</Td><Td>{v.student}</Td><Td>{v.course}</Td><Td>{v.owner}</Td><Td>{when(v.modified_time)}</Td>
    </tr>)}
  </Table>
);

export const ExpiringTable = ({ rows }) => (
  <Table testid="vc-table-expiring" empty={!rows.length} head={<><Th>Voucher (CRM)</Th><Th>Exam</Th><Th>State</Th><Th>Student</Th><Th>Expiry</Th><Th right>Days left</Th></>}>
    {rows.map(v => <tr key={v.id} className="border-t border-slate-100" data-testid={`vc-expiring-${v.id}`}>
      <Td><Ext href={v.url} testid={`vc-crm-link-${v.id}`}>{v.voucher_code || 'Open'}</Ext></Td><Td>{v.exam}</Td>
      <Td><Badge variant="outline" className={`text-[9px] ${v.state === 'available' ? 'bg-amber-50 text-amber-700' : ''}`}>{v.state}</Badge></Td>
      <Td>{v.student || '—'}</Td><Td>{v.expiry_date}</Td><Td right cls={v.days_left < 0 ? 'text-rose-600 font-semibold' : v.days_left <= 30 ? 'text-amber-600 font-semibold' : ''}>{v.days_left < 0 ? `expired ${-v.days_left}d ago` : v.days_left}</Td>
    </tr>)}
  </Table>
);
